import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import jobModerationService from '../../services/jobModerationService';
import { 
  Eye, 
  Check, 
  X,
  Clock,
  Building,
  MapPin,
  DollarSign,
  Calendar,
  Grid3X3,
  List,
  Filter,
  Zap,
  Timer,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ShieldAlert,
  FileWarning
} from 'lucide-react';

// Auto-approve countdown helper
const AutoApproveCountdown = ({ job }) => {
  const info = jobModerationService.getAutoApproveInfo(job);
  if (!info) return null;

  if (info.isOverdue) {
    return (
      <div className="flex items-center text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
        <Zap className="h-3 w-3 mr-1 flex-shrink-0" />
        <span>Auto-approval overdue</span>
      </div>
    );
  }

  const hours = Math.floor(info.hoursRemaining);
  const minutes = Math.floor((info.hoursRemaining - hours) * 60);

  return (
    <div className="flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
      <Timer className="h-3 w-3 mr-1 flex-shrink-0" />
      <span>Auto-approves in {hours}h {minutes}m</span>
    </div>
  );
};

// Content flags display component
const ContentFlagsDisplay = ({ job, compact = false }) => {
  const flags = jobModerationService.getContentFlags(job);
  if (!flags || flags.totalFlags === 0) return null;

  if (compact) {
    return (
      <div className="flex items-center text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-md">
        <ShieldAlert className="h-3 w-3 mr-1 flex-shrink-0" />
        <span>{flags.totalFlags} issue{flags.totalFlags !== 1 ? 's' : ''} flagged</span>
      </div>
    );
  }

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
      <div className="flex items-center text-sm font-medium text-orange-800">
        <ShieldAlert className="h-4 w-4 mr-2" />
        Content Filter Results ({flags.totalFlags} flag{flags.totalFlags !== 1 ? 's' : ''})
      </div>
      {flags.obsceneWords.length > 0 && (
        <div className="text-xs">
          <span className="font-medium text-red-700">Inappropriate Language:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {flags.obsceneWords.map((word, i) => (
              <span key={i} className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-xs">
                {word}
              </span>
            ))}
          </div>
        </div>
      )}
      {flags.spamIndicators.length > 0 && (
        <div className="text-xs">
          <span className="font-medium text-yellow-700">Spam Indicators:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {flags.spamIndicators.map((indicator, i) => (
              <span key={i} className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-xs">
                {indicator}
              </span>
            ))}
          </div>
        </div>
      )}
      {flags.irrelevantContent.length > 0 && (
        <div className="text-xs">
          <span className="font-medium text-orange-700">Relevance Issues:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {flags.irrelevantContent.map((issue, i) => (
              <span key={i} className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs">
                {issue}
              </span>
            ))}
          </div>
        </div>
      )}
      {flags.scannedAt && (
        <p className="text-xs text-gray-500">Scanned: {new Date(flags.scannedAt).toLocaleString()}</p>
      )}
    </div>
  );
};

// Skeleton Loading Component
const JobCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 animate-pulse">
    <div className="p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded w-20 ml-3"></div>
      </div>
      
      <div className="space-y-2 mb-4">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
      
      <div className="flex flex-col space-y-2">
        <div className="h-10 bg-gray-200 rounded"></div>
        <div className="flex space-x-2">
          <div className="flex-1 h-10 bg-gray-200 rounded"></div>
          <div className="flex-1 h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    </div>
  </div>
);

const JobModerationTab = () => {
  const { userProfile } = useAuth();
  const [allJobs, setAllJobs] = useState([]); // Store all jobs for counting
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [moderationComment, setModerationComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState('pending'); // pending, evaluation_needed, approved, rejected, all
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [notification, setNotification] = useState(null); // For user feedback
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [autoApproveLoading, setAutoApproveLoading] = useState(false);

  // Get filtered jobs for display based on current filter
  const jobs = filter === 'all' ? allJobs : allJobs.filter(j => j.moderation_status === filter);
  
  // Calculate pagination
  const totalPages = Math.ceil(jobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedJobs = jobs.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    fetchAllJobs();
  }, []);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Auto-hide notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const fetchPendingJobs = async () => {
    try {
      setLoading(true);
      
      const result = await jobModerationService.getJobsForModeration('all');
      
      if (result.success) {
        setAllJobs(result.data);
      } else {
        console.error('Error fetching jobs for moderation:', result.error);
      }
    } catch (error) {
      console.error('Error fetching jobs for moderation:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllJobs = async () => {
    try {
      setLoading(true);
      
      const result = await jobModerationService.getJobsForModeration('all');
      
      if (result.success) {
        setAllJobs(result.data);
      } else {
        console.error('Error fetching jobs for moderation:', result.error);
      }
    } catch (error) {
      console.error('Error fetching jobs for moderation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModerationAction = async (jobId, action, comment = '') => {
    try {
      setActionLoading(true);
      
      // Validate rejection comment
      if (action === 'rejected' && !comment.trim()) {
        showNotification('Please provide a reason for rejection', 'error');
        return;
      }
      
      const result = await jobModerationService.moderateJob(jobId, action, comment, userProfile.id);
      
      if (result.success) {
        // Show success notification
        const actionText = action === 'approved' ? 'approved' : 'rejected';
        showNotification(
          `Job successfully ${actionText}. Employer has been notified via email.`,
          'success'
        );
        
        // Update the allJobs list with the new data
        setAllJobs(prevJobs => {
          return prevJobs.map(job => {
            if (job.id === jobId) {
              return {
                ...job,
                ...result.data,
                moderation_status: action,
                moderated_at: new Date().toISOString(),
                moderated_by: userProfile.id,
                moderation_comment: comment
              };
            }
            return job;
          });
        });
        
        // Close modal and clear comment
        setSelectedJob(null);
        setModerationComment('');
        
        // Refresh the entire list to ensure data consistency
        setTimeout(() => {
          fetchAllJobs();
        }, 1000);
      } else {
        showNotification(
          `Failed to ${action} job: ${result.error}`,
          'error'
        );
      }
    } catch (error) {
      showNotification(
        `Error moderating job: ${error.message}`,
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTriggerAutoApprove = async () => {
    try {
      setAutoApproveLoading(true);
      const result = await jobModerationService.triggerAutoApprove();
      if (result.success) {
        const count = result.data?.approved || 0;
        showNotification(
          count > 0
            ? `Auto-approved ${count} job(s). Employers have been notified.`
            : 'No pending jobs met the auto-approve threshold.',
          'success'
        );
        if (count > 0) fetchAllJobs();
      } else {
        showNotification(`Auto-approve failed: ${result.error}`, 'error');
      }
    } catch (error) {
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setAutoApproveLoading(false);
    }
  };

  const getStatusBadge = (status, job = null) => {
    const isAutoApproved = job && jobModerationService.isAutoApproved(job);
    const isEvalNeeded = status === 'evaluation_needed';

    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: isAutoApproved ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      evaluation_needed: 'bg-orange-100 text-orange-800'
    };

    const icons = {
      evaluation_needed: <AlertTriangle className="h-3 w-3 mr-1" />,
      auto_approved: <Zap className="h-3 w-3 mr-1" />
    };

    const labels = {
      pending: 'pending',
      approved: isAutoApproved ? 'auto-approved' : 'approved',
      rejected: 'rejected',
      evaluation_needed: 'Evaluation Needed'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {isAutoApproved && icons.auto_approved}
        {isEvalNeeded && icons.evaluation_needed}
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Job Moderation</h2>
            <p className="text-sm text-gray-600">
              Review and moderate job postings from free tier employers
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <JobCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification Bar */}
      {notification && (
        <div className={`rounded-md p-4 ${
          notification.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <Check className="h-5 w-5 text-green-400" />
              ) : (
                <X className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {notification.message}
              </p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setNotification(null)}
                  className={`inline-flex rounded-md p-1.5 ${
                    notification.type === 'success' 
                      ? 'text-green-500 hover:bg-green-100' 
                      : 'text-red-500 hover:bg-red-100'
                  }`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Job Moderation</h2>
          <p className="text-sm text-gray-600">
            Review and moderate job postings from free tier employers
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Auto-Approve Trigger Button */}
          <button
            onClick={handleTriggerAutoApprove}
            disabled={autoApproveLoading}
            className="inline-flex items-center px-3 py-2 border border-purple-300 rounded-md text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50 transition-colors"
            title="Run auto-approve for all jobs pending 2+ days"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${autoApproveLoading ? 'animate-spin' : ''}`} />
            {autoApproveLoading ? 'Running...' : 'Run Auto-Approve'}
          </button>

          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${
                viewMode === 'grid'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

        {/* Filter Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { key: 'all', label: 'All Jobs', count: allJobs.length },
              { key: 'pending', label: 'Pending Review', count: allJobs.filter(j => j.moderation_status === 'pending').length },
              { key: 'evaluation_needed', label: 'Evaluation Needed', count: allJobs.filter(j => j.moderation_status === 'evaluation_needed').length },
              { key: 'approved', label: 'Approved', count: allJobs.filter(j => j.moderation_status === 'approved').length },
              { key: 'rejected', label: 'Rejected', count: allJobs.filter(j => j.moderation_status === 'rejected').length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`${
                  filter === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.key === 'evaluation_needed' && <AlertTriangle className="h-3.5 w-3.5 inline mr-1 mb-0.5" />}
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    filter === tab.key ? 'bg-blue-100 text-blue-600' 
                    : tab.key === 'evaluation_needed' && tab.count > 0 ? 'bg-orange-100 text-orange-600'
                    : 'bg-gray-100 text-gray-900'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Items Per Page Selector */}
        {jobs.length > 0 && (
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <label htmlFor="itemsPerPage" className="text-sm font-medium text-gray-700">
                Items per page:
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-14 px-3 py-1 border border-gray-300 rounded-md text-sm"
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, jobs.length)} of {jobs.length}
            </div>
          </div>
        )}

        {/* Jobs List */}
        {jobs.length === 0 ? (
          <div className="bg-white shadow-sm rounded-lg p-8 text-center">
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs to review</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filter === 'pending' ? 'All jobs have been reviewed.' : `No ${filter} jobs found.`}
            </p>
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedJobs.map((job) => (
                  <div 
                    key={job.id} 
                    onClick={() => setSelectedJob(job)}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="p-6 h-full flex flex-col justify-between">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-medium text-gray-900 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>{job.title}</h3>
                        {getStatusBadge(job.moderation_status, job)}
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        {job.moderation_status === 'pending' && (
                          <AutoApproveCountdown job={job} />
                        )}
                        {job.moderation_status === 'evaluation_needed' && (
                          <ContentFlagsDisplay job={job} compact={true} />
                        )}
                        <div className="flex items-center text-sm text-gray-500">
                          <Building className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{job.employer?.company_name || job.employer?.full_name}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">
                            {job.location}{job.is_remote && ' (Remote)'}
                          </span>
                        </div>
                        {job.budget_min && job.budget_max && (
                          <div className="flex items-center text-sm text-gray-500">
                            <DollarSign className="h-4 w-4 mr-2 flex-shrink-0" />
                            <span>${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex items-center text-xs text-gray-400">
                          <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                          Posted {formatDate(job.created_at)}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-4 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'}}>
                        {job.description}
                      </p>
                      
                      {(job.moderation_status === 'pending' || job.moderation_status === 'evaluation_needed') && (
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModerationAction(job.id, 'approved');
                            }}
                            disabled={actionLoading}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedJob(job);
                            }}
                            disabled={actionLoading}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {paginatedJobs.map((job) => (
                    <div 
                      key={job.id} 
                      onClick={() => setSelectedJob(job)}
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">{job.title}</h3>
                              <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                                <div className="flex items-center">
                                  <Building className="h-4 w-4 mr-1" />
                                  {job.employer?.company_name || job.employer?.full_name}
                                </div>
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-1" />
                                  {job.location}
                                  {job.is_remote && ' (Remote)'}
                                </div>
                                {job.budget_min && job.budget_max && (
                                  <div className="flex items-center">
                                    <DollarSign className="h-4 w-4 mr-1" />
                                    ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </div>
                            {getStatusBadge(job.moderation_status, job)}
                          </div>
                          
                          {job.moderation_status === 'pending' && (
                            <div className="mt-1">
                              <AutoApproveCountdown job={job} />
                            </div>
                          )}
                          {job.moderation_status === 'evaluation_needed' && (
                            <div className="mt-1">
                              <ContentFlagsDisplay job={job} compact={true} />
                            </div>
                          )}

                          <p className="mt-2 text-sm text-gray-600 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>
                            {job.description}
                          </p>
                          
                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="h-4 w-4 mr-1" />
                              Posted {formatDate(job.created_at)}
                            </div>
                            
                            {(job.moderation_status === 'pending' || job.moderation_status === 'evaluation_needed') && (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleModerationAction(job.id, 'approved');
                                  }}
                                  disabled={actionLoading}
                                  className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Approve
                                </button>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedJob(job);
                                  }}
                                  disabled={actionLoading}
                                  className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Reject
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            )}
          </>
        )}

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{selectedJob.title}</h3>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Job Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Location:</span>
                      <p className="text-gray-600">
                        {selectedJob.location}
                        {selectedJob.is_remote && ' (Remote)'}
                      </p>
                    </div>
                    {selectedJob.budget_min && selectedJob.budget_max && (
                      <div>
                        <span className="font-medium text-gray-700">Budget:</span>
                        <p className="text-gray-600">
                          ${selectedJob.budget_min?.toLocaleString()} - ${selectedJob.budget_max?.toLocaleString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-700">Posted:</span>
                      <p className="text-gray-600">{formatDate(selectedJob.created_at)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <div className="mt-1">{getStatusBadge(selectedJob.moderation_status, selectedJob)}</div>
                      {selectedJob.moderation_status === 'pending' && (
                        <div className="mt-1">
                          <AutoApproveCountdown job={selectedJob} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content Flags Section - shown for evaluation_needed jobs */}
                {selectedJob.moderation_status === 'evaluation_needed' && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Content Filter Flags</h4>
                    <ContentFlagsDisplay job={selectedJob} compact={false} />
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-gray-900">Employer Information</h4>
                  <p className="text-sm text-gray-600">
                    {selectedJob.employer?.company_name || selectedJob.employer?.full_name}
                  </p>
                  <p className="text-sm text-gray-500">{selectedJob.employer?.email}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">Job Description</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedJob.description}</p>
                </div>
                
                {selectedJob.requirements && (
                  <div>
                    <h4 className="font-medium text-gray-900">Requirements & Responsibilities</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedJob.requirements}</p>
                  </div>
                )}
                
                {selectedJob.qualifications && (
                  <div>
                    <h4 className="font-medium text-gray-900">Qualifications</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedJob.qualifications}</p>
                  </div>
                )}
                
                {selectedJob.skills_required && selectedJob.skills_required.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900">Required Skills</h4>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedJob.skills_required.map((skill, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {(selectedJob.moderation_status === 'pending' || selectedJob.moderation_status === 'evaluation_needed') && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      {selectedJob.moderation_status === 'evaluation_needed' 
                        ? 'Review Flagged Job' 
                        : 'Moderation Action'}
                    </h4>
                    {selectedJob.moderation_status === 'evaluation_needed' && (
                      <p className="text-sm text-orange-600 mb-2">
                        <AlertTriangle className="h-4 w-4 inline mr-1" />
                        This job was flagged by the content filter. Please review the content carefully before approving.
                      </p>
                    )}
                    <textarea
                      value={moderationComment}
                      onChange={(e) => setModerationComment(e.target.value)}
                      placeholder="Add a comment (required for rejection)..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    
                    <div className="flex justify-end space-x-3 mt-3">
                      <button
                        onClick={() => handleModerationAction(selectedJob.id, 'approved', moderationComment)}
                        disabled={actionLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve Job
                      </button>
                      
                      <button
                        onClick={() => handleModerationAction(selectedJob.id, 'rejected', moderationComment)}
                        disabled={actionLoading || !moderationComment.trim()}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject Job
                      </button>
                    </div>
                  </div>
                )}
                
                {selectedJob.moderation_comment && (
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900">Moderation Comment</h4>
                    <p className="text-sm text-gray-600">{selectedJob.moderation_comment}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobModerationTab;